import { createFileRoute, Link } from "@tanstack/react-router";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { useCategory } from "@/lib/use-catalog";
import { CategoryGrid } from "@/components/site/CategoryGrid";

export const Route = createFileRoute("/kategori/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug} — Event Sverige` },
      { name: "description", content: `Hyr produkter inom ${params.slug} hos Event Sverige.` },
      { property: "og:title", content: `${params.slug} — Event Sverige` },
      { property: "og:description", content: `Hyr produkter inom ${params.slug} hos Event Sverige — kostnadsfri intresseanmälan.` },
      { property: "og:url", content: `/kategori/${params.slug}` },
    ],
    links: [{ rel: "canonical", href: `https://eventsverige.se/kategori/${params.slug}` }],
  }),
  component: CategoryPage,
});

function CategoryPage() {
  const { slug } = Route.useParams();
  const { data, isLoading } = useCategory(slug);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Kategorin finns inte</h1>
        <Link to="/produkter" className="mt-6 inline-flex items-center gap-2 text-accent-foreground bg-accent rounded-full px-5 py-2.5 font-semibold">
          Tillbaka till sortimentet
        </Link>
      </div>
    );
  }

  const { category, products } = data;

  return (
    <div>
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-grain opacity-20" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-22">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6">
              <Sparkles className="h-3.5 w-3.5" /> {category.name}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.95]">
              {category.name}
            </h1>
            {category.description && (
              <p className="mt-6 text-lg text-primary-foreground/80 max-w-xl">
                {category.description}
              </p>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/produkter" className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-bold hover:bg-accent/90 transition">
                Se hela sortimentet <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/forfragan" className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 text-primary-foreground border border-primary-foreground/20 px-6 py-3 font-semibold hover:bg-primary-foreground/15 transition">
                Gå till förfrågan
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <CategoryGrid products={products} isLoading={false} placeholder={`Sök i ${category.name.toLowerCase()}...`} />
      </section>
    </div>
  );
}
